import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Mail, Send } from "lucide-react";
import { CONTACT_EMAIL, INSTAGRAM_URL, IMAGES } from "../data/content";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOALS = ["Perder grasa", "Ganar músculo", "Recomposición corporal", "Coaching VIP", "Otro"];

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", goal: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensaje enviado. Te responderé lo antes posible.");
      setForm({ name: "", email: "", goal: "", message: "" });
    } catch {
      toast.error("No se pudo enviar el mensaje. Inténtalo de nuevo o escribe a info@rnbcoaching.com");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0D0D0D] border border-zinc-800 px-4 py-3.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors";

  return (
    <section id="contacto" data-testid="rnb-contact-page" className="relative py-20 lg:py-32 bg-[#050505] overflow-hidden">
      <img src={IMAGES.editorial.bgRoom} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[center_30%]" />
      <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          <Reveal>
            <p className="text-xs tracking-[0.4em] text-zinc-500 uppercase">Contacto</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
              HABLEMOS <span className="text-transparent" style={{ WebkitTextStroke: "1.5px #fff" }}>DE TU CAMBIO</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 font-light leading-relaxed max-w-md">
              Cuéntame tu punto de partida y tu objetivo. Te respondo personalmente lo antes posible.
            </p>
            <div className="mt-10 space-y-5">
              <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group">
                <span className="h-11 w-11 border border-zinc-800 flex items-center justify-center group-hover:bg-white group-hover:text-black group-hover:border-white transition-colors">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="text-sm tracking-wide">{CONTACT_EMAIL}</span>
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group">
                <span className="h-11 w-11 border border-zinc-800 flex items-center justify-center group-hover:bg-white group-hover:text-black group-hover:border-white transition-colors">
                  <Instagram className="h-4 w-4" />
                </span>
                <span className="text-sm tracking-wide">@dpostigo91</span>
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="rnb-contact-form" className="border border-zinc-800 bg-[#0A0A0A] p-6 sm:p-10 space-y-5">
              <div>
                <label htmlFor="contact-name" className="block text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Nombre</label>
                <input id="contact-name" data-testid="rnb-contact-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Tu nombre" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Email</label>
                <input id="contact-email" data-testid="rnb-contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="tu@email.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-goal" className="block text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Objetivo</label>
                <select id="contact-goal" data-testid="rnb-contact-goal" value={form.goal} onChange={set("goal")} className={`${inputCls} appearance-none`}>
                  <option value="">Selecciona una opción</option>
                  {GOALS.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Mensaje</label>
                <textarea id="contact-message" data-testid="rnb-contact-message" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder="Cuéntame tu situación y qué quieres conseguir..." className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="rnb-contact-submit-button"
                className="group w-full inline-flex items-center justify-center gap-3 bg-white text-black font-display text-xl tracking-[0.15em] px-8 py-4 hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? "ENVIANDO..." : "ENVIAR MENSAJE"}
                <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="text-[11px] text-zinc-600 leading-relaxed">
                Al enviar aceptas la política de privacidad. Tus datos solo se usan para responder a tu mensaje.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
